import { useEffect, useMemo, useState, useCallback } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths } from "date-fns";
import { ChevronLeft, ChevronRight, X, Plus, Loader2, MapPin, DollarSign, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";

const ymd = (d) => format(d, "yyyy-MM-dd");
const within = (day, start, end) => day >= start && day <= end;

const STATUS_COLOR = {
  active: "bg-emerald-500",
  upcoming: "bg-blue-500",
  returned: "bg-slate-400",
  cancelled: "bg-rose-300",
  block: "bg-amber-400",
};

function BookingDetailModal({ booking, apiBase, pickupLocations, canEdit, onClose, onDone }) {
  const [pickup, setPickup] = useState(booking.pickup_location || "");
  const [amount, setAmount] = useState(booking.amount_due ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`${apiBase}/bookings/${booking.id}`, { pickup_location: pickup || undefined, amount_due: amount === "" ? undefined : Number(amount) });
      toast.success("Reservation updated.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this reservation? The dates will be freed.")) return;
    setBusy("cancel");
    try {
      await api.post(`${apiBase}/bookings/${booking.id}/cancel`, { reason });
      toast.success("Reservation cancelled.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="booking-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-lg font-semibold">{booking.car_name || "Reservation"}</h3>
            <p className="text-sm text-slate-500">{booking.customer_name} · {booking.pickup_date} → {booking.return_date}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><MapPin size={14} /> Pickup location</label>
            <select value={pickup} onChange={(e) => setPickup(e.target.value)} disabled={!canEdit} data-testid="detail-pickup" className="belo-input w-full mt-1.5 disabled:opacity-60">
              <option value="">—</option>
              {pickupLocations.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {canEdit && (
            <div>
              <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><DollarSign size={14} /> Amount due ($)</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="detail-amount" className="belo-input w-full mt-1.5" />
            </div>
          )}
          {canEdit && (
            <button onClick={save} disabled={busy === "save"} data-testid="detail-save" className="w-full rounded-full bg-blue-600 text-white py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{busy === "save" ? "Saving…" : "Save changes"}</button>
          )}
          {booking.status !== "cancelled" && (
            <div className="pt-3 border-t border-slate-100">
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason" placeholder="Cancellation reason (optional)" className="belo-input w-full mb-2 text-sm" />
              <button onClick={cancel} disabled={busy === "cancel"} data-testid="detail-cancel" className="w-full flex items-center justify-center gap-2 rounded-full border-2 border-rose-200 text-rose-600 py-2.5 text-sm font-semibold hover:bg-rose-50 disabled:opacity-50">
                <Ban size={15} /> {busy === "cancel" ? "Cancelling…" : "Cancel reservation"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateBookingModal({ preset, pickupLocations, onClose, onDone }) {
  const [f, setF] = useState({
    car_id: preset.car_id, customer_name: "", customer_email: "", customer_phone: "", date_of_birth: "",
    pickup_date: preset.date, return_date: preset.date, pickup_location: pickupLocations[0] || "",
    mark_paid: false, payment_method: "cash",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/crm/bookings", f);
      toast.success("Reservation created.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="create-booking-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">New reservation · {preset.car_name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-sm"><span className="text-slate-600">Guest name</span><input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="cb-name" className="belo-input w-full mt-1" /></label>
          <label className="text-sm"><span className="text-slate-600">Email</span><input value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} data-testid="cb-email" className="belo-input w-full mt-1" /></label>
          <label className="text-sm"><span className="text-slate-600">Phone</span><input value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} data-testid="cb-phone" className="belo-input w-full mt-1" /></label>
          <label className="text-sm"><span className="text-slate-600">Date of birth</span><input type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="cb-dob" className="belo-input w-full mt-1" /></label>
          <label className="text-sm"><span className="text-slate-600">Pickup location</span>
            <select value={f.pickup_location} onChange={(e) => set("pickup_location", e.target.value)} data-testid="cb-pickup" className="belo-input w-full mt-1">
              {pickupLocations.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label className="text-sm"><span className="text-slate-600">Pickup date</span><input type="date" value={f.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} data-testid="cb-pickup-date" className="belo-input w-full mt-1" /></label>
          <label className="text-sm"><span className="text-slate-600">Return date</span><input type="date" value={f.return_date} onChange={(e) => set("return_date", e.target.value)} data-testid="cb-return-date" className="belo-input w-full mt-1" /></label>
          <label className="col-span-2 flex items-center gap-2 text-sm mt-1"><input type="checkbox" checked={f.mark_paid} onChange={(e) => set("mark_paid", e.target.checked)} data-testid="cb-paid" /> Mark as paid now</label>
          {f.mark_paid && (
            <label className="col-span-2 text-sm"><span className="text-slate-600">Payment method</span>
              <select value={f.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="cb-method" className="belo-input w-full mt-1">
                <option value="cash">Cash</option><option value="card">Card</option><option value="zelle">Zelle</option><option value="other">Other</option>
              </select>
            </label>
          )}
        </div>
        <button onClick={submit} disabled={busy} data-testid="cb-submit" className="w-full mt-5 rounded-full bg-blue-600 text-white py-2.5 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{busy ? "Creating…" : "Create reservation"}</button>
      </div>
    </div>
  );
}

export function FleetOverviewCalendar({ apiBase = "/admin", canManage = true }) {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [data, setData] = useState(null);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [detail, setDetail] = useState(null);
  const [create, setCreate] = useState(null);
  const today = ymd(new Date());

  const start = ymd(startOfMonth(month));
  const end = ymd(endOfMonth(month));
  const days = useMemo(() => eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) }), [month]);

  const load = useCallback(() => {
    api.get(`${apiBase}/fleet-schedule`, { params: { start, end } })
      .then((r) => { setData(r.data); if (r.data.pickup_locations) setPickupLocations(r.data.pickup_locations); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [apiBase, start, end]);
  useEffect(() => { load(); }, [load]);

  // fallback pickup locations if the host endpoint doesn't return them
  useEffect(() => {
    if (pickupLocations.length === 0 && canManage) {
      api.get("/admin/fleet-schedule", { params: { start, end } }).then((r) => setPickupLocations(r.data.pickup_locations || [])).catch(() => {});
    }
  }, [pickupLocations.length, canManage, start, end]);

  const cellFor = (v, dstr) => {
    for (const b of v.bookings) {
      if (within(dstr, b.pickup_date, b.return_date)) {
        const status = b.status === "cancelled" ? "cancelled" : b.return_date < today ? "returned" : b.pickup_date <= today ? "active" : "upcoming";
        return { type: "booking", booking: { ...b, car_name: v.name }, status };
      }
    }
    for (const bl of v.blocks) {
      if (within(dstr, bl.start, bl.end)) return { type: "block", block: bl, status: "block" };
    }
    return null;
  };

  const onDone = () => { setDetail(null); setCreate(null); load(); };

  return (
    <div className="mt-4" data-testid="fleet-overview-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setMonth(addMonths(month, -1))} data-testid="overview-prev" className="grid place-items-center w-9 h-9 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronLeft size={16} /></button>
          <p className="font-heading font-semibold text-slate-900 w-40 text-center">{format(month, "MMMM yyyy")}</p>
          <button onClick={() => setMonth(addMonths(month, 1))} data-testid="overview-next" className="grid place-items-center w-9 h-9 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"><ChevronRight size={16} /></button>
        </div>
        <div className="hidden sm:flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500" /> Upcoming</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500" /> Active</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-400" /> Returned</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-400" /> Blocked</span>
        </div>
      </div>

      {!data ? (
        <p className="p-8 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading fleet…</p>
      ) : (
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-auto">
          <table className="border-collapse" data-testid="overview-grid">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-2.5 text-xs font-semibold text-slate-500 min-w-[180px] border-b border-slate-100">Vehicle</th>
                {days.map((d) => (
                  <th key={ymd(d)} className={`px-0 py-2 text-[10px] font-semibold border-b border-slate-100 min-w-[30px] ${ymd(d) === today ? "text-blue-600" : "text-slate-400"}`}>{format(d, "d")}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.vehicles.map((v) => (
                <tr key={v.car_id} data-testid={`overview-row-${v.car_id}`} className="hover:bg-slate-50/50">
                  <td className="sticky left-0 z-10 bg-white px-4 py-2 border-b border-slate-50">
                    <p className="text-sm font-medium text-slate-900 truncate max-w-[170px]">{v.name}</p>
                    <p className="text-[11px] text-slate-400">{v.stock_number ? `#${v.stock_number} · ` : ""}{v.available === false ? "Unavailable" : `$${v.weekly_rate}/wk`}</p>
                  </td>
                  {days.map((d) => {
                    const dstr = ymd(d);
                    const cell = cellFor(v, dstr);
                    const clickable = cell ? cell.type === "booking" : canManage;
                    const cls = cell ? STATUS_COLOR[cell.status] : "bg-white hover:bg-blue-50";
                    return (
                      <td key={dstr} className="p-0 border-b border-l border-slate-50 h-9">
                        <button
                          data-testid={cell?.type === "booking" ? `overview-booking-${cell.booking.id}` : `overview-cell-${v.car_id}-${dstr}`}
                          disabled={!clickable}
                          title={cell?.type === "booking" ? `${cell.booking.customer_name} · ${cell.booking.pickup_date}→${cell.booking.return_date}` : cell?.type === "block" ? (cell.block.reason || "Blocked") : ""}
                          onClick={() => {
                            if (cell?.type === "booking") setDetail(cell.booking);
                            else if (canManage && !cell) setCreate({ car_id: v.car_id, car_name: v.name, date: dstr });
                          }}
                          className={`w-full h-9 ${cls} ${clickable ? "cursor-pointer" : "cursor-default"} transition-colors`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canManage && (
        <p className="text-xs text-slate-400 mt-3">Tip: click any open (white) day to create a reservation, or click a colored bar to edit or cancel it.</p>
      )}

      {detail && <BookingDetailModal booking={detail} apiBase={apiBase} pickupLocations={pickupLocations} canEdit={canManage} onClose={() => setDetail(null)} onDone={onDone} />}
      {create && <CreateBookingModal preset={create} pickupLocations={pickupLocations} onClose={() => setCreate(null)} onDone={onDone} />}
    </div>
  );
}
